import {
  Settings2,
  Brain,
  MessagesSquare,
  FileText,
  Globe,
  Archive,
  Hand,
  Image,
  Inbox,
  Keyboard,
  ListTodo,
  Mail,
  MousePointerClick,
  MoveVertical,
  Paperclip,
  Pencil,
  Plug,
  Reply,
  Search,
  Table,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react'
import { parseMcpToolName, type Locale, type ToolStep } from '@hub/contracts'
import { i18n } from '@/i18n'
import { intlLocale } from '@/i18n/dates'

interface ToolMeta {
  icon: LucideIcon
  /** Past tense — shown once the call settled. */
  label: string
  /** Present tense — shown while running. */
  activeLabel: string
  /** Short argument summary rendered as the mono "query" chip. */
  query?: (args: Record<string, unknown>) => string | undefined
}

const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined)

/** Icon + query summary per known tool; the labels live in `chat:tools.<name>`. */
const TOOL_META = {
  vectorQueryTool: { icon: Search, query: (a) => str(a.query) },
  listTasks: { icon: ListTodo },
  listMembers: { icon: Users },
  listDocuments: { icon: Search, query: (a) => str(a.query) },
  createTask: { icon: ListTodo, query: (a) => str(a.title) },
  updateTask: { icon: Pencil, query: (a) => str(a.title) ?? str(a.taskId) },
  readDocument: { icon: FileText, query: (a) => str(a.title) ?? str(a.documentId) },
  createDocument: { icon: FileText, query: (a) => str(a.title) },
  updateDocument: { icon: Pencil, query: (a) => str(a.title) ?? str(a.documentId) },
  updateBlocks: { icon: Pencil, query: (a) => str(a.documentId) },
  replaceDocument: { icon: Pencil, query: (a) => str(a.title) ?? str(a.documentId) },
  web_search: { icon: Globe, query: (a) => str(a.query) },
  readSpreadsheetCsv: { icon: Table, query: (a) => str(a.title) ?? str(a.documentId) },
  viewImage: { icon: Image },
  listInboxEmails: { icon: Inbox, query: (a) => str(a.query) },
  readInboxEmail: { icon: Mail },
  readEmailAttachment: { icon: Paperclip },
  saveEmailAttachment: { icon: Paperclip, query: (a) => str(a.title) },
  replyToEmail: { icon: Reply },
  archiveInboxEmail: { icon: Archive },
  rememberFact: { icon: Brain, query: (a) => str(a.content) },
  recallProjectMemory: { icon: Brain, query: (a) => str(a.query) },
  forgetMemory: { icon: Brain },
  saveProjectProfile: { icon: Settings2 },
  archiveOnboardingChat: { icon: Archive },
  listChats: { icon: MessagesSquare, query: (a) => str(a.query) },
  readChat: { icon: MessagesSquare, query: (a) => str(a.threadId) },
  browserNavigate: { icon: Globe, query: (a) => str(a.url) },
  browserRead: { icon: Globe },
  browserClick: { icon: MousePointerClick, query: (a) => str(a.ref) },
  browserType: { icon: Keyboard, query: (a) => str(a.text) },
  browserPress: { icon: Keyboard, query: (a) => str(a.key) },
  browserScroll: { icon: MoveVertical, query: (a) => str(a.direction) },
  browserHandToUser: { icon: Hand, query: (a) => str(a.reason) },
  browserClose: { icon: Globe },
} satisfies Record<string, Omit<ToolMeta, 'label' | 'activeLabel'>>

type KnownTool = keyof typeof TOOL_META

function isKnownTool(name: string): name is KnownTool {
  return Object.prototype.hasOwnProperty.call(TOOL_META, name)
}

export function toolMeta(toolName: string): ToolMeta {
  // Board wwfu: a project plugin's tool, `mcp__<plugin>__<tool>` → "dev-echo · whoami".
  const mcp = parseMcpToolName(toolName)
  if (mcp) {
    const params = { server: mcp.server, tool: mcp.tool }
    return {
      icon: Plug,
      label: i18n.t('chat:tools.mcp.label', params),
      activeLabel: i18n.t('chat:tools.mcp.activeLabel', params),
    }
  }
  if (!isKnownTool(toolName)) return { icon: Wrench, label: toolName, activeLabel: toolName }
  const meta: Omit<ToolMeta, 'label' | 'activeLabel'> = TOOL_META[toolName]
  return {
    ...meta,
    label: i18n.t(`chat:tools.${toolName}.label`),
    activeLabel: i18n.t(`chat:tools.${toolName}.activeLabel`),
  }
}

function argsOf(step: ToolStep): Record<string, unknown> {
  return step.args && typeof step.args === 'object' ? (step.args as Record<string, unknown>) : {}
}

/** Parsed result (undefined while running, on error, or if the preview was truncated). */
function resultOf(step: ToolStep): Record<string, unknown> | undefined {
  if (!step.resultPreview) return undefined
  try {
    const parsed = JSON.parse(step.resultPreview) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : undefined
  } catch {
    return undefined
  }
}

const clip = (v: string, n = 48) => (v.length > n ? `${v.slice(0, n - 3)}…` : v)

export function toolQuery(step: ToolStep): string {
  const q = isKnownTool(step.toolName) ? toolMeta(step.toolName).query?.(argsOf(step)) : undefined
  // Ids are meaningless to read — prefer the title once the result is in.
  const result = resultOf(step)
  const doc = result?.document as { title?: string } | undefined
  const task = result?.task as { title?: string } | undefined
  if (q && /^(doc|tsk)_/.test(q)) return clip(doc?.title ?? task?.title ?? q)
  return q ? clip(q) : ''
}

const ARG_KEYS = [
  'query',
  'title',
  'documentId',
  'taskId',
  'content',
  'description',
  'status',
  'columnName',
  'priority',
  'assigneeId',
  'dueDate',
  'tags',
  'limit',
] as const
type ArgKey = (typeof ARG_KEYS)[number]

function argLabel(key: string): string {
  return (ARG_KEYS as readonly string[]).includes(key)
    ? i18n.t(`chat:tools.args.${key as ArgKey}`)
    : key
}

export interface DescribedRow {
  label: string
  value: string
}

/** Request arguments as readable label/value rows. */
export function describeRequest(step: ToolStep): DescribedRow[] {
  const args = argsOf(step)
  const rows: DescribedRow[] = []
  for (const [key, raw] of Object.entries(args)) {
    if (raw === undefined || raw === null || raw === '') continue
    const value =
      typeof raw === 'string'
        ? raw
        : Array.isArray(raw)
          ? raw.map(String).join(', ')
          : typeof raw === 'object'
            ? JSON.stringify(raw)
            : String(raw)
    rows.push({ label: argLabel(key), value: clip(value, 160) })
  }
  return rows
}

const formatDateTime = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(intlLocale(i18n.language as Locale), {
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
}

/** Which `chat:tools.result.count.*` noun a list-style tool's count uses. */
const COUNT_NOUNS = {
  listTasks: 'tasks',
  listDocuments: 'documents',
  listMembers: 'members',
  vectorQueryTool: 'hits',
  web_search: 'results',
  readSpreadsheetCsv: 'rows',
} as const satisfies Partial<Record<KnownTool, string>>

function countText(toolName: string, count: number): string {
  const noun =
    (COUNT_NOUNS as Partial<Record<string, (typeof COUNT_NOUNS)[keyof typeof COUNT_NOUNS]>>)[
      toolName
    ] ?? 'items'
  return i18n.t(`chat:tools.result.count.${noun}`, { count })
}

export interface DescribedResult {
  summary: string
  lines?: string[]
}

/** The tool's return value, summarised per tool. */
export function describeResult(step: ToolStep): DescribedResult | undefined {
  if (step.status === 'running') return { summary: i18n.t('chat:tools.result.waiting') }
  if (step.error) return { summary: step.error.message }

  const sum = step.resultSummary
  if (sum) {
    if (sum.error) return { summary: sum.error }
    if (sum.document) {
      const d = sum.document
      const meta: string[] = []
      if (d.words) meta.push(i18n.t('chat:tools.result.words', { count: d.words }))
      else if (d.chars) meta.push(i18n.t('chat:tools.result.chars', { count: d.chars }))
      if (d.updatedAt)
        meta.push(i18n.t('chat:tools.result.updated', { date: formatDateTime(d.updatedAt) }))
      if (d.createdAt)
        meta.push(i18n.t('chat:tools.result.created', { date: formatDateTime(d.createdAt) }))
      const summary =
        step.toolName === 'createDocument'
          ? i18n.t('chat:tools.result.createdDocument', { title: d.title })
          : step.toolName === 'updateDocument'
            ? i18n.t('chat:tools.result.updatedDocument', { title: d.title })
            : d.title
      return {
        summary,
        lines: [...(meta.length ? [meta.join(' · ')] : []), ...(d.excerpt ? [d.excerpt] : [])],
      }
    }
    if (sum.task) {
      const verb =
        step.toolName === 'createTask'
          ? i18n.t('chat:tools.result.createdTask', { title: sum.task.title })
          : i18n.t('chat:tools.result.updatedTask', { title: sum.task.title })
      const col = sum.task.columnName ? ` · ${sum.task.columnName}` : ''
      return { summary: `${verb}${col}` }
    }
    if (sum.count !== undefined) {
      const prefix =
        step.toolName === 'readSpreadsheetCsv' ? i18n.t('chat:tools.result.csvPrefix') : ''
      return {
        summary: `${prefix}${countText(step.toolName, sum.count)}${sum.note ? ` · ${sum.note}` : ''}`,
        lines: sum.items?.length ? sum.items : sum.header ? [sum.header] : undefined,
      }
    }
  }

  // Fallback: parse the (possibly truncated) preview.
  const done = i18n.t('chat:tools.result.done')
  const r = resultOf(step)
  if (!r) return step.resultPreview ? { summary: clip(step.resultPreview, 300) } : { summary: done }
  if (typeof r.error === 'string' && r.error) return { summary: r.error }
  const keys = Object.keys(r)
  return {
    summary: keys.length
      ? i18n.t('chat:tools.result.responseWith', { keys: keys.join(', ') })
      : done,
  }
}

export function toolRequestText(step: ToolStep): string {
  if (step.args !== undefined) {
    try {
      return JSON.stringify(step.args, null, 2)
    } catch {
      /* fall through */
    }
  }
  return step.argsText ?? '–'
}

export function toolResultText(step: ToolStep): string {
  if (step.error) return step.error.message
  if (!step.resultPreview) return step.status === 'running' ? '…' : i18n.t('chat:tools.result.done')
  try {
    return JSON.stringify(JSON.parse(step.resultPreview), null, 2)
  } catch {
    return step.resultPreview
  }
}
